import { useEffect, useRef } from 'react';

import { FieldTypes } from 'constants/field-types-enum';
import {
  getDependents,
  getDependentsFromValues,
  runDependentValidators,
  runDynamicDependents,
  runHiddenDependents,
} from 'helpers/candidate_application/dependent-field-processing';
import {
  buildAllFormValues,
  pickByPrefixes,
} from 'helpers/candidate_application/form-page-helpers';
import { convertObjectToDotPaths } from 'helpers/field-paths';
import { useDispatch, useSelector } from 'react-redux';

import {
  recalculateIdRequirements,
  selectCandidateApplication,
  updateCandidateSections,
  updateHiddenValues,
} from 'api/candidate-application-slice';

// Initial dynamic processing when existing values are loaded
export function useInitialProcessing({
  fetched,
  sectionKey,
  currentPagePrefixes,
  reset,
  getValues,
  trigger,
  setError,
  clearErrors,
  check_values,
  control,
}) {
  const didInitRef = useRef(false);
  const dispatch = useDispatch();
  const { candidateFields, application } = useSelector(selectCandidateApplication);
  const id_uploads = application?.id_uploads ?? [];
  const other_uploads = application?.other_uploads ?? [];
  const allUploads = [...id_uploads, ...other_uploads];

  const {
    sections = [],
    hiddenFields = {},
    fieldPathIndex = {},
    candidateValues = {},
    wrapperValidators = {},
    dynamicDependencyCodes = {},
    hiddenDependencyCodes = {},
    validationDependencyCodes = {},
    idRequirementsDependencyCodes = [],
    templateSources = {},
  } = candidateFields || {};

  const pageHasRegisteredValues = () => {
    const vals = getValues() || {};
    const keys = Object.keys(vals);
    return currentPagePrefixes?.some(p => keys.some(k => k === p || k.startsWith(`${p}.`)));
  };

  const extractDefaultValuesFromFields = () => {
    const defaultValues = {};

    if (!sections || !currentPagePrefixes || currentPagePrefixes?.length === 0) {
      return defaultValues;
    }

    // Find the current section
    const currentSection = (sections || [])?.find(section => section.code === sectionKey);
    if (!currentSection || !currentSection?.subsections) {
      return defaultValues;
    }

    // Create a set of prefixes for quick lookup
    const prefixSet = new Set(currentPagePrefixes);

    // Recursive function to extract default values from fields
    const extractFromFields = fields => {
      if (!fields || !Array.isArray(fields)) return;

      (fields || [])?.forEach(field => {
        if (!field || !field.code) return;

        // Check if this field matches any of the current page prefixes
        const fieldCode = field.code;
        const matchesPrefix =
          prefixSet.has(fieldCode) ||
          (Array.from(prefixSet) || [])?.some(prefix => fieldCode.startsWith(`${prefix}.`));

        // Only include default if it exists and field matches current page
        if (matchesPrefix && field?.defaultValue !== undefined && field?.defaultValue !== null) {
          defaultValues[fieldCode] = field.defaultValue;
        }

        // Recursively process subFields (for array/object fields)
        if (field?.subFields && Array.isArray(field.subFields)) {
          extractFromFields(field.subFields);
        }
        // altIdFields: same as subFields – each group has subFields (defaults, etc.)
        const altIdGroups = field?.altIdFields || field?.alt_id_fields;
        if (Array.isArray(altIdGroups)) {
          altIdGroups.forEach(group => {
            const inner = group.subFields || group.sub_fields;
            if (Array.isArray(inner)) extractFromFields(inner);
          });
        }
      });
    };

    // Process all subsections in the current section
    (currentSection?.subsections || [])?.forEach(subsection => {
      if (subsection.fields && Array.isArray(subsection.fields)) {
        extractFromFields(subsection.fields);
      }
    });

    return defaultValues;
  };

  const extractUploadValuesFromRedux = () => {
    const uploadValues = {};
    if (!currentPagePrefixes?.length || !allUploads?.length) return uploadValues;
    const currentSection = (sections || [])?.find(s => s.code === sectionKey);
    if (!currentSection?.subsections) return uploadValues;
    const prefixSet = new Set(currentPagePrefixes);
    const isUploadType = type => type === FieldTypes.DocumentUpload || type === FieldTypes.Upload;

    const extractFromFields = fields => {
      if (!fields || !Array.isArray(fields)) return;
      (fields || []).forEach(field => {
        if (!field?.code) return;
        const matchesPrefix =
          prefixSet.has(field.code) ||
          (Array.from(prefixSet) || []).some(p => field.code.startsWith(`${p}.`));
        if (matchesPrefix && isUploadType(field.type)) {
          const upload = allUploads.find(u => u?.metadata?.field_code === field.code);
          if (upload?.id != null) uploadValues[field.code] = upload.id;
        }
        if (field?.subFields?.length) extractFromFields(field.subFields);
        (field?.altIdFields || field?.alt_id_fields || []).forEach(group => {
          const inner = group?.subFields || group?.sub_fields;
          if (Array.isArray(inner)) extractFromFields(inner);
        });
      });
    };
    (currentSection.subsections || []).forEach(sub => extractFromFields(sub?.fields));
    return uploadValues;
  };

  // Define processDependencies function outside of effects
  const processDependencies = () => {
    // Get existing values for current section from both candidateValues and formValues
    const candidatePageValues = pickByPrefixes(candidateValues || {}, currentPagePrefixes);
    const formPageValues = pickByPrefixes(
      candidateFields?.formValues?.[sectionKey] || {},
      currentPagePrefixes,
    );

    // Merge values, giving precedence to formValues (more recent)
    const defaultValuesFromFields = extractDefaultValuesFromFields();
    const uploadValuesFromRedux = extractUploadValuesFromRedux();

    const pageValues = {
      ...defaultValuesFromFields,
      ...uploadValuesFromRedux, // Fill from Redux uploads when not yet in candidate/form (reload or navigate back)
      ...candidatePageValues,
      ...formPageValues,
    };

    // This ensures existing values are loaded when navigating back to a section
    if (Object.keys(pageValues).length > 0) {
      reset(pageValues, { keepDefaultValues: true });
    }

    // Build a fresh "allValues" snapshot AFTER reset
    const allValues = buildAllFormValues({
      candidateFields,
      watchValues: getValues(), // Get current form state after reset (defaults + existing)
      currentPagePrefixes,
    });

    let allValuesWithHiddenValues = null;
    // A) Hidden (compute hidden values). Run up to two passes so hidden fields that depend on
    // other hidden values (e.g. fathers_name_required uses client_countries_to_check) get correct values.
    {
      const hiddenDeps = getDependentsFromValues(
        /* add __processor_params__ to fields runtime config if you want the processor to run on initial load
          regardless of which values are present in the form - see countries to check fields for an example
        */
        { ...allValues, __processor_params__: true },
        hiddenDependencyCodes,
        {
          includeIndexes: false, // hidden fields don't have indexes
          ignoreEmpty: true,
        },
      );
      if (hiddenDeps.length) {
        let valuesForHidden = allValues;
        let accumulatedPatch = {};
        const maxPasses = 2;
        for (let pass = 0; pass < maxPasses; pass++) {
          const hiddenPatch = runHiddenDependents(hiddenDeps, valuesForHidden, hiddenFields);
          if (!hiddenPatch) break;
          accumulatedPatch = { ...accumulatedPatch, ...hiddenPatch };
          valuesForHidden = { ...valuesForHidden, ...hiddenPatch };
        }
        if (Object.keys(accumulatedPatch).length > 0) {
          dispatch(updateHiddenValues(accumulatedPatch));
          allValuesWithHiddenValues = { ...allValues, ...accumulatedPatch };
        }
      }
    }

    // Convert to dot paths once for all initial passes
    const dotValues = convertObjectToDotPaths(allValuesWithHiddenValues || {});

    // B) Dynamic (alters sections (field definitions) only, not values)
    {
      let latestSections = sections;
      Object.entries(dotValues).forEach(([path, value]) => {
        const dynamicDeps = getDependents(path, dynamicDependencyCodes);
        if (dynamicDeps.length) {
          const updatedSections = runDynamicDependents(
            dynamicDeps,
            latestSections,
            fieldPathIndex,
            value,
            allValuesWithHiddenValues,
            templateSources,
          );
          if (updatedSections) {
            latestSections = updatedSections;
          }
        }
      });
      if (latestSections !== sections) {
        dispatch(updateCandidateSections(latestSections));
      }
    }

    // C) Validators (limit to current page) - These NEED fields to be registered
    const fieldsRegistered = pageHasRegisteredValues();

    const validationDeps = getDependentsFromValues(
      allValuesWithHiddenValues,
      validationDependencyCodes,
      {
        includeIndexes: true, // need indexes for matching to values which have real indexes
        ignoreEmpty: true,
      },
    );
    if (validationDeps.length) {
      runDependentValidators(
        validationDeps,
        currentPagePrefixes,
        wrapperValidators,
        allValuesWithHiddenValues,
        trigger,
        setError,
        clearErrors,
      );
    }

    // D) ID Requirements (check if any existing field affects ID requirements)
    {
      const relevantFields = Object.keys(dotValues).filter(fieldName =>
        idRequirementsDependencyCodes.includes(fieldName),
      );

      if (relevantFields.length > 0) {
        // Pass the current allValues to ensure default values are included
        dispatch(recalculateIdRequirements({ latestFormValues: allValuesWithHiddenValues }));
      }
    }

    // Return true if all dependencies processed, false if validators were skipped
    return fieldsRegistered;
  };

  useEffect(() => {
    didInitRef.current = false;
  }, [fetched, currentPagePrefixes]);

  useEffect(() => {
    // Guard: only once per section + when fetched
    if (didInitRef.current) {
      return;
    }
    if (!fetched) {
      return;
    }

    // Check if we have values to populate (from candidateValues, formValues, or Redux uploads)
    const candidatePageValues = pickByPrefixes(candidateValues || {}, currentPagePrefixes);
    const formPageValues = pickByPrefixes(
      candidateFields?.formValues?.[sectionKey] || {},
      currentPagePrefixes,
    );
    const uploadValuesFromRedux = extractUploadValuesFromRedux();
    const hasValuesToPopulate =
      Object.keys(candidatePageValues).length > 0 ||
      Object.keys(formPageValues).length > 0 ||
      Object.keys(uploadValuesFromRedux).length > 0;

    // Get current form values (includes defaults)
    const currentFormValues = getValues();

    // If form doesn't have any values yet AND we don't have values to populate, wait
    if (Object.keys(currentFormValues).length === 0 && !hasValuesToPopulate) {
      return;
    }

    // Call processDependencies (it will handle reset even if fields aren't registered yet)
    const processed = processDependencies();

    if (processed) {
      didInitRef.current = true;
    } else if (hasValuesToPopulate) {
      // Fields weren't registered yet, but we have values (candidate/form/upload) - retry after a short delay
      const timeoutId = setTimeout(() => {
        const retryProcessed = processDependencies();
        if (retryProcessed) {
          didInitRef.current = true;
        }
      }, 100);
      return () => clearTimeout(timeoutId);
    }
  }, [
    fetched,
    currentPagePrefixes,
    sectionKey,
    reset,
    getValues,
    trigger,
    setError,
    clearErrors,
    dispatch,
    // Note: candidateFields, sections, etc. are accessed from selector and don't need to be in deps
    // Including them causes circular updates when processDependencies dispatches changes
  ]);

  // Helper to check if a specific field is registered
  const isFieldRegistered = fieldName => {
    if (!control?._fields) return false;
    // Check if field is registered in react-hook-form's internal state
    return control._fields[fieldName] !== undefined;
  };

  // Separate effect for rejected values - runs independently to handle check_values changes
  useEffect(() => {
    if (!fetched) {
      return;
    }

    // Check if we should set rejected errors
    if (!check_values?.rejected || !check_values?.rejected_values?.values?.length) return;

    const rejectedFields = check_values?.rejected_values?.values;
    const errorMessage =
      'Our team requested a change for this field. Please check and update before submitting.';

    // Check which fields are registered
    const registeredFields = rejectedFields?.filter(fieldName => isFieldRegistered(fieldName));
    const unregisteredFields = rejectedFields?.filter(fieldName => !isFieldRegistered(fieldName));

    // Set errors for already registered fields immediately
    if (registeredFields.length > 0) {
      registeredFields.forEach(rejectedValue => {
        setError(rejectedValue, {
          type: 'manual',
          message: errorMessage,
        });
      });
    }

    // Retry for unregistered fields with exponential backoff
    if (unregisteredFields.length > 0) {
      let retryCount = 0;
      const maxRetries = 10; // Try up to 10 times
      const baseDelay = 100; // Start with 100ms

      const trySetErrors = () => {
        // Check current registration state for the unregistered fields
        const nowRegistered = unregisteredFields.filter(fieldName => isFieldRegistered(fieldName));

        // Set errors for fields that are now registered
        nowRegistered.forEach(rejectedValue => {
          setError(rejectedValue, {
            type: 'manual',
            message: errorMessage,
          });
        });

        // Check if there are still unregistered fields
        const stillUnregistered = unregisteredFields.filter(
          fieldName => !isFieldRegistered(fieldName),
        );

        // If all fields are registered or max retries reached, stop
        if (stillUnregistered.length === 0 || retryCount >= maxRetries) {
          return;
        }

        retryCount++;
        const delay = baseDelay * Math.min(retryCount, 5); // Cap at 500ms
        setTimeout(trySetErrors, delay);
      };

      const timeoutId = setTimeout(trySetErrors, baseDelay);
      return () => clearTimeout(timeoutId);
    }
  }, [check_values, fetched, currentPagePrefixes, setError, control]);
}
